import { computed, ref, watch, type Ref } from 'vue';
import type Conversation from '@/entities/Conversation.ts';
import type { Message } from '@/entities/types/ConversationType.ts';

export const SMS_SCROLL_SETTLE_DELAY = 300;

interface UseSmsUnreadBoundaryOptions {
    selectedConversation: Ref<Conversation | null | undefined>;
    selectedConversationMessages: Ref<Message[]>;
    selectedConversationLastMessageReadIdAtLoad: Ref<number | null>;
    hasFetchedAllMessages: Ref<boolean>;
    initialLoading: Ref<boolean>;
    scrollToTopToLoadUnreadBoundary: () => void;
    seekMessageInHistory?: (
        predicate: (messages: Message[]) => boolean,
        options?: { maxPages?: number }
    ) => Promise<boolean>;
}

export const useSmsUnreadBoundary = (options: UseSmsUnreadBoundaryOptions) => {
    const hasSeenUnreadSeparator = ref(false);
    const scrollToUnreadSeparatorSignal = ref(0);
    let unreadBoundaryScrollTimeout: ReturnType<typeof setTimeout> | null = null;
    const isSeeking = ref(false);

    const markUnreadSeparatorAsSeen = (): void => {
        hasSeenUnreadSeparator.value = true;
    };

    const resetUnreadSeparatorSeen = (): void => {
        hasSeenUnreadSeparator.value = false;
    };

    const clearPendingUnreadBoundaryScroll = (): void => {
        if (unreadBoundaryScrollTimeout === null) {
            return;
        }

        clearTimeout(unreadBoundaryScrollTimeout);
        unreadBoundaryScrollTimeout = null;
    };

    const hasCurrentUserReadState = computed<boolean>(() => {
        const conversation = options.selectedConversation.value;

        if (!conversation?.currentUserId) {
            return false;
        }

        return conversation.conversationUsers.some(
            (conversationUser) => conversationUser.userId === conversation.currentUserId
        );
    });

    const firstUnreadMessageId = computed((): number | null => {
        const messages = options.selectedConversationMessages.value;
        const lastReadId = options.selectedConversationLastMessageReadIdAtLoad.value;

        if (!messages.length || !hasCurrentUserReadState.value) {
            return null;
        }

        if (lastReadId === null) {
            if (!options.hasFetchedAllMessages.value) {
                return null;
            }

            return messages.find((message) => !message.senderIsAuthUser)?.id ?? null;
        }

        const readIndex = messages.findIndex((message) => message.id === lastReadId);

        if (readIndex === -1) {
            return null;
        }

        const firstUnread = messages
            .slice(readIndex + 1)
            .find((message) => !message.senderIsAuthUser);

        return firstUnread?.id ?? null;
    });

    const isLastReadMessageOutsideLoadedRange = computed<boolean>(() => {
        const messages = options.selectedConversationMessages.value;

        if (!messages.length || !hasCurrentUserReadState.value) {
            return false;
        }

        if (options.selectedConversationLastMessageReadIdAtLoad.value === null) {
            return !options.hasFetchedAllMessages.value;
        }

        return !messages.some(
            (message) => message.id === options.selectedConversationLastMessageReadIdAtLoad.value
        );
    });

    const shouldShowStickyUnreadSeparator = computed<boolean>(() => {
        if (options.initialLoading.value) {
            return false;
        }

        if (isLastReadMessageOutsideLoadedRange.value) {
            return true;
        }

        return !!firstUnreadMessageId.value && !hasSeenUnreadSeparator.value;
    });

    const onUnreadSeparatorVisibilityChange = (visible: boolean): void => {
        if (visible) {
            markUnreadSeparatorAsSeen();
        }
    };

    const onStickyUnreadSeparatorClick = async (): Promise<void> => {
        const lastReadId = options.selectedConversationLastMessageReadIdAtLoad.value;

        if (isSeeking.value) {
            return;
        }

        if (!isLastReadMessageOutsideLoadedRange.value) {
            scrollToUnreadSeparatorSignal.value++;
            return;
        }

        if (!options.seekMessageInHistory) {
            options.scrollToTopToLoadUnreadBoundary();
            return;
        }

        isSeeking.value = true;

        try {
            const hasResolvedUnreadBoundary = await options.seekMessageInHistory(
                lastReadId === null
                    ? () => options.hasFetchedAllMessages.value
                    : (messages) => messages.some((message) => message.id === lastReadId),
                { maxPages: 10 }
            );

            if (!hasResolvedUnreadBoundary && lastReadId === null) {
                options.scrollToTopToLoadUnreadBoundary();
            }
        } finally {
            isSeeking.value = false;
        }
    };

    watch(
        () => options.selectedConversation.value?.id,
        () => {
            clearPendingUnreadBoundaryScroll();
            resetUnreadSeparatorSeen();
            isSeeking.value = false;
        }
    );

    watch(
        isLastReadMessageOutsideLoadedRange,
        (isOutside, wasOutside, onCleanup) => {
            clearPendingUnreadBoundaryScroll();

            if (!isOutside && wasOutside) {
                unreadBoundaryScrollTimeout = setTimeout(() => {
                    scrollToUnreadSeparatorSignal.value++;
                    unreadBoundaryScrollTimeout = null;
                }, SMS_SCROLL_SETTLE_DELAY);

                onCleanup(() => {
                    clearPendingUnreadBoundaryScroll();
                });
            }
        },
        { immediate: true }
    );

    return {
        hasSeenUnreadSeparator: computed(() => hasSeenUnreadSeparator.value),
        hasCurrentUserReadState,
        firstUnreadMessageId,
        isLastReadMessageOutsideLoadedRange,
        shouldShowStickyUnreadSeparator,
        scrollToUnreadSeparatorSignal,
        onStickyUnreadSeparatorClick,
        onUnreadSeparatorVisibilityChange,
        isSeeking: computed(() => isSeeking.value),
    };
};
